// Rider money — what they hold in credit, what they have spent, and every credit an
// admin has issued them.
//
// Was a collapsed accordion of seven label/value pairs, a "Manage Credits" button, and
// a credit log whose every entry was a four-line block. Now a `SectionCard`: the
// balances as `Figure`s, the log as hairline rows with the amount on the trailing edge
// where it can be scanned down. Manage Credits moves into the section header, still
// behind the `credits` permission, and still opening the same dialog.
import { useCallback, useEffect, useState } from 'react';
import { Box, Skeleton, Pagination } from '../../../ui/core';
import { DollarBadgeIcon } from '../../../ui/icons';
import { scrollY, hairline } from '../../../ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatters } from '../../../hooks';
import { getCreditLog, getRideHistory, getRiderCashFlow } from '../../../redux/Users/actions';
import ManageCreditsDialog from './ManageCreditsDialog';
import { useHasPermission } from '../../../helpers/api/auth/index';
import { SectionCard, SectionLabel, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  Amount,
  Figure,
  Figures,
  TrailingMeta,
  RowAction,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';

function formatEnumLabel(value) {
  if (!value) {
    return '-';
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const RiderCashFlowCard = ({ user }) => {
  const dispatch = useDispatch();
  const userRideHistory = useSelector((state) => state.users.selection.rideHistory);
  const f = useFormatters();
  const data = useSelector((state) => state.users.selection.riderCashFlow);
  const creditLog = useSelector((state) => state.users.selection.creditLog);
  const [isManageCreditsDialogOpen, setIsManageCreditsDialogOpen] = useState(false);
  const [creditLogPage, setCreditLogPage] = useState(1);

  const hasCreditsPermission = useHasPermission('credits');
  const creditLogPageSize = 5;
  const creditLogPages = creditLog ? Math.ceil(creditLog.count / creditLogPageSize) : 0;

  // Page 3 of one rider's credit log is not page 3 of the next one's. Reset during render
  // rather than in an effect: an effect would let a fetch for the new user go out against
  // the old page first, which is the double-fetch the two refs here used to be dodging.
  const [pagedUserId, setPagedUserId] = useState(user.id);
  if (pagedUserId !== user.id) {
    setPagedUserId(user.id);
    setCreditLogPage(1);
  }

  const loadCashFlow = useCallback(() => getRiderCashFlow(user.id)(dispatch), [user.id, dispatch]);
  const [cashFlowStatus, retryCashFlow] = useSectionFetch(loadCashFlow);

  const loadCreditLog = useCallback(
    () => getCreditLog(user.id, (creditLogPage - 1) * creditLogPageSize, creditLogPageSize)(dispatch),
    [user.id, creditLogPage, creditLogPageSize, dispatch],
  );
  const [creditLogStatus, retryCreditLog] = useSectionFetch(loadCreditLog);

  // Not this section's data: ManageCreditsDialog needs a ride to attach a credit to.
  useEffect(() => {
    getRideHistory(user.id, 0, 5)(dispatch);
  }, [user.id, dispatch]);

  const refreshCreditLog = async ({ resetPage = false } = {}) => {
    // Page 1 already, so nothing would change identity and re-fire the fetch.
    if (resetPage && creditLogPage !== 1) {
      setCreditLogPage(1);
      return;
    }

    await retryCreditLog();
  };

  // Signed, so the log reads as movement rather than as a column of sums: an issued
  // credit is green and a revoked one is not. `Amount` colours off the leading '+'.
  const signed = (amount) => `${amount > 0 ? '+' : ''}${f.number.currency(amount)}`;

  return (
    <>
      <SectionCard
        title="Rider money"
        icon={<DollarBadgeIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
        meta={
          hasCreditsPermission && (
            <TrailingMeta>
              <RowAction onClick={() => setIsManageCreditsDialogOpen(true)}>Manage credits</RowAction>
            </TrailingMeta>
          )
        }
      >
        {/* Credit first and tinted, because "how much do they have" is the question
            this section is opened for. The four totals behind it are context. */}
        <Figures>
          <Figure label="General credit" value={f.number.currency(user.creditAmount)} palette="success" />
          <Figure label="Cancellation credit" value={f.number.currency(user.cancellationCredit)} />
          <Figure label="Referral credit" value={f.number.currency(user.referralCredit)} />
          {/* The three credit balances come off the user document, so they are always
              here. Volume and Tipped are the fetched pair — an em dash when that call
              failed, rather than a skeleton that never resolves or a $0 that is a lie. */}
          <Figure
            label="Volume"
            value={
              cashFlowStatus === 'loading' ? (
                <Skeleton variant="text" width={64} />
              ) : data ? (
                f.number.currency(data.volume)
              ) : (
                '—'
              )
            }
            note={
              data ? `${f.number.decimal(data.totalRides)} rides · ${f.number.decimal(data.totalSeats)} seats` : null
            }
          />
          <Figure
            label="Tipped"
            value={
              cashFlowStatus === 'loading' ? (
                <Skeleton variant="text" width={64} />
              ) : data ? (
                f.number.currency(data.totalTipped)
              ) : (
                '—'
              )
            }
          />
        </Figures>

        {cashFlowStatus === 'failed' && (
          <SectionError onRetry={retryCashFlow}>Couldn&rsquo;t load this rider&rsquo;s spend.</SectionError>
        )}

        <Box sx={{ px: 1.75, pt: 1.25, pb: 0.5, borderTop: (t) => `1px solid ${hairline(t)}` }}>
          <SectionLabel>Credit log</SectionLabel>
        </Box>

        {creditLogStatus === 'loading' && <SectionSkeleton />}
        {creditLogStatus === 'failed' && (
          <SectionError onRetry={retryCreditLog}>Couldn&rsquo;t load the credit log.</SectionError>
        )}
        {/* Admin-issued credit only — cancellation, promo and referral credits are
            written by the backend and never reach `credit_audit_log`. "No admin-issued
            credits" rather than "no credits" is the difference. */}
        {creditLogStatus === 'done' && !creditLog?.data?.length && (
          <EmptyRows>No admin-issued credits found.</EmptyRows>
        )}
        {creditLogStatus === 'done' && creditLog?.data?.length > 0 && (
          <Box sx={{ maxHeight: 320, ...scrollY }}>
            {creditLog.data.map((entry, i) => (
              <Row
                key={entry.id}
                first={i === 0}
                // The gutter is 62px, which is a date and not a datetime — the full
                // timestamp goes on the meta line rather than wrapping the column.
                at={f.date.toLocaleString(entry.createdAt, f.date.DateFormats.DATE_SHORT)}
                label={`${formatEnumLabel(entry.creditType)} · ${formatEnumLabel(entry.reason)}`}
                meta={[
                  entry.description,
                  entry.rideId && `Ride ${entry.rideId}`,
                  entry.issuedByAdminEmail || 'Unknown admin',
                ]
                  .filter(Boolean)
                  .join(' · ')}
                right={<Amount value={signed(entry.amount)} />}
              />
            ))}
          </Box>
        )}
        {creditLogPages > 1 && (
          <Box sx={{ px: 1.75, py: 1.25, borderTop: (t) => `1px solid ${hairline(t)}` }}>
            <Pagination
              count={creditLogPages}
              page={creditLogPage}
              size="small"
              onChange={(_, page) => setCreditLogPage(page)}
            />
          </Box>
        )}
      </SectionCard>

      <ManageCreditsDialog
        userData={user}
        userRideHistory={userRideHistory}
        isOpen={isManageCreditsDialogOpen}
        onClose={() => setIsManageCreditsDialogOpen(false)}
        onCreditsUpdated={() => refreshCreditLog({ resetPage: true })}
        // TODO: Add ride selection on next iteration
        rideItem={null}
      />
    </>
  );
};

export default RiderCashFlowCard;
